import { Link } from "react-router-dom";
import { Play, Radio, Headphones as HeadphonesIcon, Film, ArrowUpRight } from "lucide-react";
import { HOST_PHOTO_1, PODCAST_INFO, PLATFORMS, TOPICS, SOCIALS } from "@/data/site";
import { YouTubeIcon, SpotifyIcon, AppleIcon, AmazonIcon, IHeartIcon, YouTubeMusicIcon } from "@/components/icons/Platforms";
import Waveform from "@/components/podcast/Waveform";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";
import Spotlight from "@/components/podcast/Spotlight";
import { StudioMic, Headphones, CinemaCamera } from "@/components/podcast/StudioGear";

const ICONS = {
  YouTube: YouTubeIcon,
  "YouTube Music": YouTubeMusicIcon,
  Spotify: SpotifyIcon,
  "Apple Podcasts": AppleIcon,
  "Amazon Music": AmazonIcon,
  iHeartRadio: IHeartIcon,
};

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 pb-20 md:pb-32 overflow-hidden bg-[#0A0A0A] film-grain"
    >
      {/* Spotlight beams from the top */}
      <Spotlight color="#E10600" position="top-left" width={700} opacity={0.18} rotate={-15} />
      <Spotlight color="#1E6FE0" position="top-right" width={700} opacity={0.18} rotate={15} />

      {/* Studio iconography backdrop */}
      <StudioBackdrop opacity={0.22} size={280} />

      {/* Decorative large studio gear silhouettes */}
      <StudioMic
        className="absolute -left-20 top-40 w-80 hidden lg:block float-slow"
        color="#FFFFFF"
        strokeWidth={1}
        style={{ opacity: 0.06 }}
      />
      <Headphones
        className="absolute -right-24 bottom-16 w-[28rem] hidden lg:block float-medium"
        color="#1E6FE0"
        strokeWidth={1}
        style={{ opacity: 0.08 }}
      />
      <CinemaCamera
        className="absolute right-1/4 -top-6 w-72 hidden xl:block"
        color="#E10600"
        strokeWidth={1}
        style={{ opacity: 0.07 }}
      />

      {/* Red ♀ + Blue ♂ glows */}
      <div
        className="absolute top-1/4 -right-32 w-[500px] h-[500px] rounded-full opacity-35 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(225,6,0,0.45) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -left-32 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(30,111,224,0.5) 0%, transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left content */}
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-gold mb-8 bg-[#C9A24A]/5">
              <span className="w-2 h-2 bg-[#C9A24A] pulse-dot rounded-full" />
              <span
                data-testid="hero-live-tag"
                className="text-[10px] md:text-xs tracking-[0.25em] uppercase font-bold gold-shimmer"
              >
                {PODCAST_INFO.schedule}
              </span>
            </div>

            <p className="text-xs tracking-[0.3em] uppercase font-bold text-zinc-500 font-mono mb-4 flex items-center gap-3">
              <span className="symbol-male text-base">♂</span>
              The Relationship Podcast
              <span className="symbol-female text-base">♀</span>
            </p>

            <h1
              data-testid="hero-title"
              className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem] leading-[0.85] uppercase tracking-tight"
            >
              Prove
              <br />
              <span className="text-[#1E6FE0]">Your</span>{" "}
              <span className="text-[#E10600]">Point</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-xl leading-relaxed">
              {PODCAST_INFO.tagline}
            </p>
            <p className="mt-3 text-sm text-zinc-500 max-w-xl">
              <span className="text-[#1E6FE0] font-bold">What men think.</span>{" "}
              <span className="text-[#E10600] font-bold">What women feel.</span>{" "}
              What nobody else will say out loud. Hosted by{" "}
              <span className="text-white font-bold">Manuel Santana</span> ·
              Filmed in <span className="text-white">NYC & Boston</span>.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {TOPICS.slice(0, 6).map((t, i) => (
                <span
                  key={t}
                  className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-1 border ${
                    i % 2 === 0
                      ? "border-[#E10600]/40 text-[#E10600]"
                      : "border-[#1E6FE0]/40 text-[#1E6FE0]"
                  }`}
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/episodes"
                data-testid="hero-cta-listen"
                className="btn-primary"
              >
                <Play className="w-4 h-4" fill="currentColor" />
                Play Latest Episode
              </Link>
              <Link
                to="/be-a-guest"
                data-testid="hero-cta-guest"
                className="btn-blue"
              >
                Apply To Be A Guest
              </Link>
              <a
                href={SOCIALS.imdb}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-imdb"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#F5C518] text-black font-bold tracking-[0.15em] uppercase text-xs hover:bg-[#FFD43B] transition-colors"
              >
                <Film className="w-4 h-4" />
                View On IMDb
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-10">
              <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 font-mono mb-3 flex items-center gap-2">
                <HeadphonesIcon className="w-3 h-3 text-[#1E6FE0]" />
                Available On
              </p>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => {
                  const Icon = ICONS[p.name];
                  return (
                    <a
                      key={p.name}
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`hero-platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group flex items-center gap-2 px-3 py-2 border border-[#262626] hover:border-[#E10600] transition-colors"
                    >
                      {Icon && <Icon className="w-4 h-4" style={{ color: p.color }} />}
                      <span className="text-xs font-bold tracking-wider uppercase text-zinc-300 group-hover:text-white">
                        {p.name}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Host photo as "Now Playing" card */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="absolute -bottom-3 -right-3 w-full h-full border-2 border-[#E10600] z-0 hidden md:block" />
              <div className="absolute -top-3 -left-3 w-full h-full border-2 border-[#1E6FE0] z-0 hidden md:block" />

              <div className="absolute -top-4 left-4 z-20 bg-[#E10600] px-3 py-1.5 flex items-center gap-2 light-sweep">
                <Radio className="w-3.5 h-3.5 text-white" />
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white">
                  Now Recording
                </span>
              </div>
              {/* Gold premium SEASON badge */}
              <div className="absolute -top-4 right-4 z-20 premium-tag">
                <span>Season 02 · Premium</span>
              </div>

              <div className="relative bg-[#0A0A0A] border border-[#262626] overflow-hidden">
                <img
                  data-testid="hero-host-photo"
                  src={HOST_PHOTO_1}
                  alt="Manuel Santana — Host of Prove Your Point Podcast"
                  className="w-full h-auto block"
                />
                {/* Sharp brand wash */}
                <div
                  className="absolute inset-0 mix-blend-multiply opacity-25 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(225,6,0,0.6) 0%, transparent 50%, rgba(30,111,224,0.6) 100%)",
                  }}
                />

                {/* Bottom overlay with show meta */}
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/70 to-transparent">
                  <p className="text-[10px] tracking-[0.3em] uppercase font-mono text-[#E10600]">
                    Hosted By
                  </p>
                  <p className="font-display text-3xl uppercase leading-none text-white mt-1">
                    Manuel Santana
                  </p>
                  <p className="mt-1 text-xs text-zinc-400">
                    NYC · Boston · Mon 7PM EST
                  </p>
                </div>
              </div>

              {/* mini waveform under */}
              <div className="mt-4 px-1">
                <Waveform bars={56} height={36} color="#E10600" muted="#1E6FE0" />
                <div className="flex justify-between mt-2 text-[10px] font-mono text-zinc-500">
                  <span>00:00</span>
                  <span className="text-[#E10600]">
                    ▶ S2 E01 · What Men Want. What Women Need.
                  </span>
                  <span>1:24:08</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
